import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PlayArrow as ProcessIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { invoiceService, INVOICE_BASKETS, PROCESSING_STATUS, PO_VALIDATION_STATUS } from '../services/invoice.service';
import InvoiceApiHealthCheck from './InvoiceProcessing/InvoiceApiHealthCheck';
import DashboardTiles from './InvoiceProcessing/DashboardTiles';
import DocumentList from './InvoiceProcessing/DocumentList';
import DocumentDetails from './InvoiceProcessing/DocumentDetails';
import ProcessingPage from './InvoiceProcessing/ProcessingPage';
import Analytics from './InvoiceProcessing/Analytics';

/**
 * Main Invoice Processing Component
 * Provides a comprehensive interface for invoice processing operations
 */
const InvoiceProcessing = () => {
  // State management
  const [documents, setDocuments] = useState([]);
  const [baskets, setBaskets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBasket, setSelectedBasket] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [processingStatus, setProcessingStatus] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [pipelineOverview, setPipelineOverview] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  
  // Dashboard navigation state
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'list', 'details', 'upload', 'processing', 'analytics'
  const [viewBasket, setViewBasket] = useState(null);
  const [viewDocumentId, setViewDocumentId] = useState(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load initial data
  useEffect(() => {
    loadBaskets();
    loadDocuments();
    loadPipelineOverview();
  }, []);

  // Load documents when basket filter changes
  useEffect(() => {
    loadDocuments();
  }, [selectedBasket]);

  // Navigation handlers
  const handleViewDocuments = (basket) => {
    setViewBasket(basket);
    setCurrentView('list');
  };

  const handleViewDocument = (documentId) => {
    setViewDocumentId(documentId);
    setCurrentView('details');
  };

  /**
   * Handle PDF review
   */
  const handlePdfReview = (documentId) => {
    setViewDocumentId(documentId);
    setCurrentView('details');
    // Set a flag to indicate PDF should be opened automatically
    setPdfPreviewOpen(true);
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setViewBasket(null);
    setViewDocumentId(null);
    setPdfPreviewOpen(false);
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setViewDocumentId(null);
    setPdfPreviewOpen(false);
  };

  const handleUploadView = () => {
    setCurrentView('upload');
    setUploadSuccess(null); // Clear any previous upload success state
  };

  const handleClearUploadSuccess = () => {
    setUploadSuccess(null);
  };

  const handleProcessingView = (documentId) => {
    setViewDocumentId(documentId);
    setCurrentView('processing');
  };

  const handleProcessingComplete = (results) => {
    setProcessingResults(results);
    // Refresh data to show updated status
    loadBaskets();
    loadDocuments();
    // Navigate to document details to show the processed results
    setCurrentView('details');
  };

  const handleProcessingRefresh = () => {
    // Refresh data when processing completes
    loadBaskets();
    loadDocuments();
    // Trigger refresh in DocumentList component
    setRefreshTrigger(prev => prev + 1);
  };

  const handleBackFromProcessing = () => {
    setCurrentView('details');
    setViewDocumentId(null);
  };

  const handleAnalyticsView = () => {
    setCurrentView('analytics');
  };

  const handleProcessDocument = async (documentId) => {
    // Navigate to processing page instead of processing directly
    handleProcessingView(documentId);
  };

  const handleProcessAllDocuments = async () => {
    try {
      setLoading(true);
      await invoiceService.processAllDocuments();
      showSnackbar('Batch processing started successfully', 'success');
      // Refresh data
      loadBaskets();
      loadDocuments();
    } catch (error) {
      showSnackbar(`Failed to start batch processing: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load all baskets with document counts
   */
  const loadBaskets = async () => {
    try {
      const response = await invoiceService.getBaskets();
      setBaskets(response.baskets || []);
    } catch (error) {
      console.error('Failed to load baskets:', error);
      showSnackbar('Failed to load baskets: ' + error.message, 'error');
    }
  };

  /**
   * Load documents with optional basket filtering
   */
  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await invoiceService.getDocuments(selectedBasket || null);
      setDocuments(response.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
      showSnackbar('Failed to load documents: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load pipeline overview data
   */
  const loadPipelineOverview = async () => {
    try {
      // This would be implemented when the endpoint is available
      // const response = await invoiceService.getPipelineOverview();
      // setPipelineOverview(response);
    } catch (error) {
      console.error('Failed to load pipeline overview:', error);
    }
  };

  /**
   * Handle file upload
   */
  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) {
      showSnackbar('Only PDF files are allowed', 'error');
      return;
    }

    try {
      setLoading(true);
      setUploadSuccess(null); // Clear previous success state
      const file = acceptedFiles[0];
      const response = await invoiceService.uploadInvoice(file);
      
      // Set upload success state for prominent display
      setUploadSuccess({
        documentId: response.document_id,
        filename: file.name,
        timestamp: new Date().toLocaleString()
      });
      
      showSnackbar(`Invoice uploaded successfully! Document ID: ${response.document_id}`, 'success');
      loadBaskets();
      loadDocuments();
    } catch (error) {
      console.error('Upload failed:', error);
      showSnackbar('Upload failed: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Process a single document
   */
  const processDocument = async (documentId) => {
    try {
      setLoading(true);
      const response = await invoiceService.processDocument(documentId);
      if (response.success) {
        showSnackbar('Document processed successfully!', 'success');
      } else {
        showSnackbar('Document processing failed', 'error');
      }
      loadBaskets();
      loadDocuments();
    } catch (error) {
      console.error('Processing failed:', error);
      showSnackbar('Processing failed: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get PO validation color
   */
  const getPOValidationColor = (status) => {
    switch (status) {
      case 'valid':
        return 'success';
      case 'invalid':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  /**
   * Show snackbar notification
   */
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Handle snackbar close
   */
  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: '', severity: 'info' });
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* API Health Check */}
      <InvoiceApiHealthCheck />

      {/* Main Content based on current view */}
      {currentView === 'dashboard' && (
        <DashboardTiles
          onViewDocuments={handleViewDocuments}
          onProcessDocuments={handleProcessAllDocuments}
          onRefresh={() => {
            loadBaskets();
            loadDocuments();
          }}
          onUploadView={handleUploadView}
          onAnalyticsView={handleAnalyticsView}
        />
      )}

      {currentView === 'list' && (
        <DocumentList
          basket={viewBasket}
          onBack={handleBackToDashboard}
          onViewDocument={handleViewDocument}
          onProcessDocument={handleProcessDocument}
          onPdfReview={handlePdfReview}
          onUploadView={handleUploadView}
          refreshTrigger={refreshTrigger}
          onBasketChange={handleViewDocuments}
          onBackToDashboard={handleBackToDashboard}
        />
      )}

      {currentView === 'details' && (
        <DocumentDetails
          documentId={viewDocumentId}
          onBack={viewBasket ? handleBackToList : handleBackToDashboard}
          onProcessDocument={handleProcessDocument}
          basketName={viewBasket}
          pdfPreviewOpen={pdfPreviewOpen}
          onPdfPreviewClose={() => setPdfPreviewOpen(false)}
          onBackToDashboard={handleBackToDashboard}
          onBackToList={handleBackToList}
        />
      )}

      {currentView === 'processing' && (
        <ProcessingPage
          documentId={viewDocumentId}
          onBack={handleBackFromProcessing}
          onNext={handleProcessingComplete}
          onRefresh={handleProcessingRefresh}
        />
      )}

      {currentView === 'upload' && (
        <Box>
          {/* Upload Header */}
          <Box display="flex" alignItems="center" mb={3}>
            <IconButton onClick={handleBackToDashboard} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              Upload Invoice Document
            </Typography>
          </Box>

          {/* Upload Success Confirmation */}
          {uploadSuccess && (
            <Card sx={{ mb: 3, border: '2px solid', borderColor: 'success.main' }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <SuccessIcon sx={{ color: 'success.main', mr: 2, fontSize: 32 }} />
                  <Typography variant="h5" color="success.main" sx={{ fontWeight: 'bold' }}>
                    Upload Successful!
                  </Typography>
                </Box>
                <Box sx={{ ml: 4 }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>File:</strong> {uploadSuccess.filename}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Document ID:</strong> {uploadSuccess.documentId}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Uploaded:</strong> {uploadSuccess.timestamp}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Your document has been successfully uploaded and is ready for processing.
                  </Typography>
                </Box>
                <Box display="flex" gap={2} mt={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ViewIcon />}
                    onClick={() => {
                      setViewDocumentId(uploadSuccess.documentId);
                      setCurrentView('details');
                    }}
                  >
                    View Document
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleClearUploadSuccess}
                  >
                    Upload Another
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Upload Dropzone */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  backgroundColor: isDragActive ? 'primary.light' : 'grey.50',
                  transition: 'all 0.2s ease-in-out',
                  opacity: loading ? 0.6 : 1,
                  '&:hover': {
                    borderColor: loading ? 'grey.300' : 'primary.main',
                    backgroundColor: loading ? 'grey.50' : 'primary.light'
                  }
                }}
              >
                <input {...getInputProps()} disabled={loading} />
                {loading ? (
                  <Box>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Uploading...
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please wait while your file is being uploaded
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      {isDragActive ? 'Drop the PDF here...' : 'Drag & drop a PDF invoice here'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      or click to select a file
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Only PDF files are supported
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Upload Instructions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upload Instructions
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Upload PDF invoice documents for AI-powered processing
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • The system will extract key information including invoice number, amounts, dates, and line items
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Documents will be automatically validated against purchase orders
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Processing results will be available in the document list
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {currentView === 'analytics' && (
        <Analytics onBack={handleBackToDashboard} />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceProcessing;